import React, { useEffect, useMemo, useState } from "react";
import { endpoints, asset } from "@/lib/api";
import { PageHeader, CTASection } from "@/components/site/Bits";
import { AnimatePresence, motion } from "framer-motion";

const FILTERS = [
  "All", "Weddings", "Corporate", "Embassy", "Museum", "Hotel", "Private Home",
  "Rooftop", "Fashion", "Cocktails", "Syphons", "Staff", "Tablescape", "Details",
];

export default function Gallery() {
  const [media, setMedia] = useState([]);
  const [active, setActive] = useState("All");

  useEffect(() => {
    endpoints.media({ limit: 200 }).then(setMedia).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (active === "All") return media;
    return media.filter((m) => (m.tags || []).includes(active));
  }, [media, active]);

  return (
    <div data-testid="gallery-page">
      <PageHeader
        eyebrow="Gallery"
        title={["Moments,", "quietly composed."]}
        intro="A record of evenings hosted and imagined — from embassy halls to garden terraces."
        testid="gallery-header"
      />

      <div className="sticky top-[68px] z-30 border-y border-aria-travertine bg-aria-ivory/95 backdrop-blur-md">
        <div className="aria-container no-scrollbar flex gap-6 overflow-x-auto py-5">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setActive(f)}
              data-testid={`gallery-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              className={`whitespace-nowrap text-[0.7rem] uppercase tracking-[0.2em] transition-colors ${
                active === f ? "text-aria-ink" : "text-aria-muted hover:text-aria-ink"
              }`}
            >
              {f}
              {active === f && <span className="ml-2 inline-block h-1 w-1 rounded-full bg-aria-brass align-middle" />}
            </button>
          ))}
        </div>
      </div>

      <section className="aria-container py-16 md:py-24" data-testid="gallery-grid">
        <div className="columns-1 gap-6 sm:columns-2 lg:columns-3 [column-fill:_balance]">
          <AnimatePresence mode="popLayout">
            {filtered.map((m) => (
              <motion.figure
                key={m.id}
                layout
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="mb-6 break-inside-avoid overflow-hidden bg-aria-travertine"
              >
                <img
                  src={asset(m.url)}
                  alt={m.alt}
                  loading="lazy"
                  className="w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.76,0,0.24,1)] hover:scale-[1.04]"
                />
              </motion.figure>
            ))}
          </AnimatePresence>
        </div>
        {filtered.length === 0 && (
          <p className="py-24 text-center font-display text-2xl text-aria-muted">No images in this collection yet.</p>
        )}
      </section>

      <CTASection />
    </div>
  );
}
